import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Buildings, MapPin, UsersThree, ArrowRight, CheckCircle, Sparkle, IdentificationCard,
} from "@phosphor-icons/react";

const STEPS = [
  { key: "branch",     label: "Add HQ",         icon: Buildings,    blurb: "Set up your first office location" },
  { key: "department", label: "Add department", icon: MapPin,       blurb: "Engineering, Sales, HR…" },
  { key: "employee",   label: "Add team",       icon: UsersThree,   blurb: "Invite your first hire" },
];

export default function SetupWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const r = await api.get("/setup-wizard/status");
      setStatus(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  if (loading) return <AppShell title="Welcome"><div className="p-8 text-zinc-500">Loading…</div></AppShell>;
  if (!status?.applicable) {
    return (
      <AppShell title="Welcome">
        <div className="p-8 text-zinc-600">Setup wizard is for company workspaces. Head back to <Link to="/app" className="underline">your dashboard</Link>.</div>
      </AppShell>
    );
  }

  const allDone = status.next_step === "done";

  return (
    <AppShell title="Set up your workspace">
      <div className="max-w-3xl mx-auto py-4 space-y-8" data-testid="setup-wizard-root">
        <header className="text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-900 border border-amber-200 rounded-full text-xs font-medium tracking-wide uppercase">
            <Sparkle size={12} weight="fill"/> First-run setup
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-4 text-zinc-950">
            Welcome to {status.company_name || "SaarOne"}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
          </h1>
          <p className="text-zinc-500 mt-3 max-w-xl mx-auto text-[15px] leading-relaxed">
            Three quick steps and your workspace is ready to start tracking attendance, leave, payroll and everything else.
          </p>
        </header>

        {/* Progress rail */}
        <ol className="grid grid-cols-3 gap-3" data-testid="setup-steps">
          {STEPS.map((s, idx) => {
            const done = status.checks[s.key];
            const active = !done && status.next_step === s.key;
            const Icon = s.icon;
            return (
              <li key={s.key} className={`relative border rounded-lg p-4 transition-all ${done ? "border-emerald-300 bg-emerald-50/60" : active ? "border-zinc-900 bg-white shadow-sm" : "border-zinc-200 bg-zinc-50/60"}`} data-testid={`step-card-${s.key}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center ${done ? "bg-emerald-600 text-white" : active ? "bg-zinc-950 text-white" : "bg-zinc-200 text-zinc-500"}`}>
                    {done ? <CheckCircle size={16} weight="fill"/> : <Icon size={14} weight="bold"/>}
                  </div>
                  <div className="text-xs text-zinc-400 font-mono">Step {idx + 1}</div>
                </div>
                <div className="font-semibold text-sm mt-3 text-zinc-900">{s.label}</div>
                <div className="text-xs text-zinc-500 mt-1 leading-snug">{s.blurb}</div>
                {done && <div className="text-xs text-emerald-700 mt-2 font-medium">✓ Done</div>}
              </li>
            );
          })}
        </ol>

        {/* Active step card */}
        {!allDone && (
          <div className="bg-white border border-zinc-200 rounded-xl p-6 md:p-8 shadow-sm" data-testid="setup-active-step">
            {status.next_step === "branch" && <BranchStep onDone={refresh}/>}
            {status.next_step === "department" && <DepartmentStep onDone={refresh}/>}
            {status.next_step === "employee" && <EmployeeStep onDone={() => navigate("/app/employees")}/>}
          </div>
        )}

        {allDone && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center" data-testid="setup-complete">
            <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto"/>
            <h2 className="font-display text-2xl font-bold mt-4">You're all set. 🎉</h2>
            <p className="text-zinc-600 mt-2">Your workspace is configured and ready to use.</p>
            <div className="flex items-center justify-center gap-3 mt-6">
              <Link to="/app"><Button className="bg-zinc-950 hover:bg-zinc-800 h-10 px-5" data-testid="setup-go-dashboard">Go to dashboard</Button></Link>
              <Link to="/app/employees"><Button variant="outline" className="h-10 px-5" data-testid="setup-go-employees">View team</Button></Link>
            </div>
          </div>
        )}

        {!allDone && (
          <div className="text-center text-xs text-zinc-400">
            <button onClick={async () => { await api.post("/setup-wizard/complete"); toast.success("You can revisit setup any time from your profile menu."); navigate("/app"); }} className="underline hover:text-zinc-700" data-testid="setup-skip">
              I'll set this up later — take me to the dashboard
            </button>
          </div>
        )}
      </div>
    </AppShell>
  );
}


// ────────────── Step 1: HQ branch ──────────────

function BranchStep({ onDone }) {
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({ name: "Headquarters", city: "", address: "", country_id: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/org/countries");
        setCountries(r.data);
        if (r.data?.length === 1) setForm(p => ({ ...p, country_id: r.data[0].id }));
      } catch { /* self-heal will populate Asia/India */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.country_id) return toast.error("Branch name and country are required.");
    setBusy(true);
    try {
      await api.post("/org/branches", { name: form.name, parent_id: form.country_id, city: form.city, address: form.address });
      toast.success("HQ branch added.");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="branch-step-form">
      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Step 1 of 3</div>
        <h2 className="font-display text-2xl font-bold mt-1 text-zinc-950">Add your headquarters.</h2>
        <p className="text-sm text-zinc-500 mt-2">Every team, payroll cycle and approval rule is anchored to a branch. Start with where you're based — you can add more later.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider font-medium text-zinc-600">Branch name *</Label>
          <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Headquarters" className="mt-2 h-10" data-testid="branch-name-input"/>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-medium text-zinc-600">Country *</Label>
          <Select value={form.country_id} onValueChange={(v) => setForm(p => ({ ...p, country_id: v }))}>
            <SelectTrigger className="mt-2 h-10" data-testid="branch-country-select"><SelectValue placeholder="Pick a country"/></SelectTrigger>
            <SelectContent>{countries.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-medium text-zinc-600">City</Label>
          <Input value={form.city} onChange={(e) => setForm(p => ({ ...p, city: e.target.value }))} placeholder="Mumbai" className="mt-2 h-10" data-testid="branch-city-input"/>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-medium text-zinc-600">Address</Label>
          <Input value={form.address} onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))} placeholder="42 MG Road, Suite 200" className="mt-2 h-10" data-testid="branch-address-input"/>
        </div>
      </div>
      <Button type="submit" disabled={busy} className="bg-zinc-950 hover:bg-zinc-800 h-10 px-5 gap-2" data-testid="branch-submit">
        {busy ? "Adding…" : "Add HQ branch"} <ArrowRight size={14}/>
      </Button>
    </form>
  );
}

// ────────────── Step 2: Department ──────────────

function DepartmentStep({ onDone }) {
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({ name: "", branch_id: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get("/org/branches");
      setBranches(r.data);
      if (r.data?.length === 1) setForm(p => ({ ...p, branch_id: r.data[0].id }));
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.branch_id) return toast.error("Department name and branch are required.");
    setBusy(true);
    try {
      await api.post("/org/departments", { name: form.name, parent_id: form.branch_id });
      toast.success("Department added.");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const presets = ["Engineering", "Sales", "Marketing", "Operations", "Finance", "Human Resources", "Customer Support"];

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="dept-step-form">
      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Step 2 of 3</div>
        <h2 className="font-display text-2xl font-bold mt-1 text-zinc-950">Create your first department.</h2>
        <p className="text-sm text-zinc-500 mt-2">Departments help you group teams for payroll, leave policies and approvals.</p>
      </div>
      <div className="flex flex-wrap gap-2 mb-2">
        {presets.map(p => (
          <button key={p} type="button" onClick={() => setForm(f => ({ ...f, name: p }))}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${form.name === p ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`}
            data-testid={`dept-preset-${p.toLowerCase().replace(/\s/g, "-")}`}>
            {p}
          </button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider font-medium text-zinc-600">Department name *</Label>
          <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Engineering" className="mt-2 h-10" data-testid="dept-name-input"/>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-medium text-zinc-600">Branch *</Label>
          <Select value={form.branch_id} onValueChange={(v) => setForm(p => ({ ...p, branch_id: v }))}>
            <SelectTrigger className="mt-2 h-10" data-testid="dept-branch-select"><SelectValue placeholder="Pick a branch"/></SelectTrigger>
            <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Button type="submit" disabled={busy} className="bg-zinc-950 hover:bg-zinc-800 h-10 px-5 gap-2" data-testid="dept-submit">
        {busy ? "Adding…" : "Add department"} <ArrowRight size={14}/>
      </Button>
    </form>
  );
}

// ────────────── Step 3: Employee ──────────────

function EmployeeStep({ onDone }) {
  return (
    <div className="space-y-5" data-testid="employee-step">
      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Step 3 of 3</div>
        <h2 className="font-display text-2xl font-bold mt-1 text-zinc-950">Add your first team member.</h2>
        <p className="text-sm text-zinc-500 mt-2">We'll take you to the Directory where you can add as many as you need. Each one gets a welcome email with their login.</p>
      </div>
      <div className="flex items-center gap-3 p-4 bg-zinc-50 border border-zinc-200 rounded-lg">
        <IdentificationCard size={28} className="text-zinc-700"/>
        <div className="text-sm text-zinc-700 leading-snug">
          The directory is where you'll manage every hire. Add at least one to mark setup complete.
        </div>
      </div>
      <Button onClick={onDone} className="bg-zinc-950 hover:bg-zinc-800 h-10 px-5 gap-2" data-testid="employee-go-directory">
        Open Directory <ArrowRight size={14}/>
      </Button>
    </div>
  );
}
